import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle, Zap, Rocket, TrendingUp, Building } from 'lucide-react';
import { usersAPI } from '../services/api';

const planDetails = {
  free: { name: 'Free Trial', price: 0, minutes: 10, icon: Zap, color: 'from-gray-500 to-gray-600' },
  starter: { name: 'Starter', price: 15, minutes: 143, icon: Zap, color: 'from-blue-500 to-blue-600' },
  jump_starter: { name: 'Jump Starter', price: 30, minutes: 316, icon: Rocket, color: 'from-teal-500 to-teal-600' },
  early_deployers: { name: 'Early Deployers', price: 36, minutes: 471, icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
  growth: { name: 'Growth', price: 200, minutes: 2857, icon: Building, color: 'from-orange-500 to-orange-600' }
};

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: ''
  });
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if a plan was selected from pricing page
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan && planDetails[storedPlan]) {
      setSelectedPlan(storedPlan);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Create end user account (not sub-admin)
      await usersAPI.signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        plan: selectedPlan,
        minutes_remaining: planDetails[selectedPlan]?.minutes || 10
      });

      // Clear stored plan
      localStorage.removeItem('selectedPlan');
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/client');
      }, 2000);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const plan = planDetails[selectedPlan];
  const PlanIcon = plan?.icon || Zap;

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Account Created!</h2>
            <p className="text-gray-400 mb-2">Your account has been created successfully.</p>
            <p className="text-teal-400 text-sm">You have {plan?.minutes || 10} minutes to test Voice AI</p>
            <p className="text-gray-500 text-sm mt-4">Redirecting to client portal...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="text-2xl font-bold text-white tracking-tight">
            AI<span className="text-teal-400">CONNEQTORS</span>
          </a>
          <p className="text-gray-500 mt-2">Create your Voice AI account</p>
        </div>

        {/* Selected Plan Display */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${plan?.color || 'from-gray-500 to-gray-600'} flex items-center justify-center`}>
                <PlanIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">{plan?.name || 'Free Trial'}</p>
                <p className="text-gray-500 text-sm">{plan?.minutes || 10} minutes included</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white font-bold">${plan?.price || 0}<span className="text-gray-500 text-sm">/mo</span></p>
              <a href="/pricing" className="text-teal-400 text-xs hover:underline">Change plan</a>
            </div>
          </div>
        </div>

        {/* Signup Card */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Get started for free</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                placeholder="Enter your full name"
                required
                data-testid="signup-name"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                placeholder="Enter your email"
                required
                data-testid="signup-email"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">Company Name (Optional)</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                placeholder="Enter your company name"
                data-testid="signup-company"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                  placeholder="Create a password (min 6 characters)"
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">Confirm Password *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                placeholder="Confirm your password"
                required
                data-testid="signup-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-6"
              data-testid="signup-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <a href="/client" className="text-teal-400 hover:underline">Sign in</a>
            </p>
          </div>

          <p className="text-gray-600 text-xs text-center mt-4">
            By signing up, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>

        {/* Footer */}
        <p className="text-gray-600 text-xs text-center mt-6">
          © 2024 AIConneqtors. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default SignUp;
